import { useBusinessPlanGate } from '@ltvco/refresh-lib/hooks';
import { BusinessGatedFeatureModal } from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useCallback, useMemo, type ComponentProps } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  BUSINESS_GATED_FEATURE_MODAL_CONTENT,
  BUSINESS_GATED_ROUTE_BY_PATH,
  type BusinessGatedFeatureKey,
  type BusinessGatedFeatureModalContent,
} from './businessGatedFeatureModal.constants';

export interface UseBusinessGatedFeatureModalResult {
  activeFeature: BusinessGatedFeatureKey | null;
  isOpen: boolean;
  modalProps: BusinessGatedFeatureModalContent | null;
  onClose: ComponentProps<typeof BusinessGatedFeatureModal>['onClose'];
  onPrimaryAction: () => void;
}

export function useBusinessGatedFeatureModal(): UseBusinessGatedFeatureModalResult {
  const navigate = useNavigate();
  const { pathname, key: locationKey } = useLocation();
  const { isGated, isLoading } = useBusinessPlanGate();
  // RTRN-1949 sells bulk property search via an add-on/PDF bundle, not a
  // business plan upgrade — suppress the business-gated modal for that path.
  const isInPdfBulkSearchVariation = useFeatureIsOn('RTRN-1949');

  const activeFeature = BUSINESS_GATED_ROUTE_BY_PATH[pathname] ?? null;
  const modalProps = activeFeature
    ? BUSINESS_GATED_FEATURE_MODAL_CONTENT[activeFeature]
    : null;
  const shouldSuppressBusinessGate =
    isInPdfBulkSearchVariation && activeFeature === 'bulk-property-search';
  const isOpen =
    Boolean(activeFeature) &&
    !isLoading &&
    isGated === true &&
    !shouldSuppressBusinessGate;

  const onClose = useCallback(() => {
    if (locationKey === 'default') {
      navigate('/dashboard');
      return;
    }

    navigate(-1);
  }, [locationKey, navigate]);

  const onPrimaryAction = useCallback(() => {
    navigate('/upgrade/plan?custom_type=business');
  }, [navigate]);

  return useMemo(
    () => ({
      activeFeature,
      isOpen,
      modalProps,
      onClose,
      onPrimaryAction,
    }),
    [activeFeature, isOpen, modalProps, onClose, onPrimaryAction]
  );
}
